import AdminTable from 'common/AdminTable';
import { getRequest, postRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const initialFormData = {
  holiday_date: '',
  type: 'closed',
  status: 'active'
};

const typeLabels = {
  closed: 'Closed',
  restricted: 'Restricted',
  all: 'All'
};

const HolidayCalendar = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest('holiday-calendar?limit=1000');
      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load holiday calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.holiday_date) {
      toast.error('Holiday date is required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await patchRequest(`holiday-calendar/${editingId}`, formData);
        toast.success('Holiday updated successfully');
      } else {
        await postRequest('holiday-calendar', formData);
        toast.success('Holiday added successfully');
      }
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      holiday_date: row.holiday_date ? String(row.holiday_date).slice(0, 10) : '',
      type: row.type || 'closed',
      status: row.status || 'active'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggle = async (id) => {
    try {
      await patchRequest(`holiday-calendar/${id}/status`, {
        status: records.find((r) => r.id === id)?.status === 'active' ? 'inactive' : 'active'
      });
      toast.success('Status updated successfully');
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This holiday will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteRequest(`holiday-calendar/${id}`);
        toast.success('Holiday deleted successfully');
        if (editingId === id) resetForm();
        fetchRecords();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to delete holiday');
      }
    });
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: 'Date',
      accessor: 'holiday_date',
      Cell: ({ row }) => (row.original.holiday_date ? String(row.original.holiday_date).slice(0, 10) : '—')
    },
    {
      Header: 'Type',
      accessor: 'type',
      Cell: ({ row }) => (
        <span
          className={`badge ${row.original.type === 'closed' ? 'badge-danger' : row.original.type === 'restricted' ? 'badge-warning' : 'badge-info'}`}
        >
          {typeLabels[row.original.type] || row.original.type}
        </span>
      )
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <button
          type="button"
          className={`btn btn-sm ${row.original.status === 'active' ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => handleToggle(row.original.id)}
        >
          {row.original.status === 'active' ? 'Active' : 'Inactive'}
        </button>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-sm btn-info mx-1 my-1" onClick={() => handleEdit(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger mx-1 my-1" onClick={() => handleDelete(row.original.id)}>
            Delete
          </button>
        </>
      )
    }
  ];

  return (
    <div className="container-fluid px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <h2 className="m-0">Holiday Calendar</h2>
          <small className="text-muted">Manage hospital holidays and restricted days</small>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5 mb-4">
          <div className="card shadow border-0 h-100">
            <div className="card-header bg-primary text-white py-3">
              <h5 className="mb-0">{editingId ? 'Update Holiday' : 'Add Holiday'}</h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="holiday_date" className="adminlables">
                    Holiday Date *
                  </label>
                  <input
                    type="date"
                    id="holiday_date"
                    name="holiday_date"
                    className="form-control"
                    value={formData.holiday_date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="type" className="adminlables">
                    Type *
                  </label>
                  <select id="type" name="type" className="form-control" value={formData.type} onChange={handleChange}>
                    <option value="closed">Closed</option>
                    <option value="restricted">Restricted</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label htmlFor="status" className="adminlables">
                    Status
                  </label>
                  <select id="status" name="status" className="form-control" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  {editingId && (
                    <button type="button" className="btn btn-light px-4" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-5" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow border-0">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Holiday List</h5>
            </div>
            <div className="card-body p-0">
              {loading && records.length === 0 ? (
                <div className="text-center p-5">Loading...</div>
              ) : (
                <AdminTable tableHeaders={tableHeaders} tableData={records} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidayCalendar;
